import type {
  Relationship,
  Side,
} from '@/contexts/FamilyGraphContext';

import type {
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

import type {
  CanonicalTreeViewerProjectionResult,
  CanonicalViewerBranch,
  CanonicalViewerConnectionKind,
  CanonicalViewerProjectionConfidence,
} from './CanonicalTreeViewerProjection';

export interface CanonicalTreeGeometryRole {
  readonly memberId: string;
  readonly generation: number;
  readonly tier: number;
  readonly side: Side;

  /** Canonical viewer branch; never inferred from presentation labels. */
  readonly branch:
    CanonicalViewerBranch;

  readonly connectionKind:
    CanonicalViewerConnectionKind;

  /**
   * Canonical viewer-relative kinship derived from structural traversal.
   *
   * This is not a stored display-label authority.
   */
  readonly kinshipRelationship:
    Relationship;

  readonly anchorMemberId:
    string | null;
  readonly anchorEdgeId:
    string | null;
  readonly sourceEdgeIds:
    readonly string[];
  readonly ancestryEdgeIds:
    readonly string[];
  readonly affiliationEdgeIds:
    readonly string[];
  readonly confidence:
    CanonicalViewerProjectionConfidence;
  readonly requiresCanonicalConnector:
    boolean;
  readonly permitsNeedsConnection:
    false;
}

export interface CanonicalTreeGeometryRoleResult {
  readonly roleByMemberId:
    ReadonlyMap<
      string,
      CanonicalTreeGeometryRole
    >;
  readonly roles:
    readonly CanonicalTreeGeometryRole[];
  readonly missingProjectionMemberIds:
    readonly string[];
  readonly mutatesCanonicalGraph:
    false;
}

export interface CanonicalTreeGeometryRoleInput {
  readonly projection:
    CanonicalTreeViewerProjectionResult | null;
  readonly treeVisibleMemberIds:
    ReadonlySet<string>;
  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];
}

const canonicalConnectorKinds =
  new Set<
    CanonicalViewerConnectionKind
  >([
    'co-parent',
    'step-family',
    'in-law',
    'guardian',
    'foster-family',
    'chosen-family',
    'god-family',
    'affiliated-family',
  ]);

export const resolveCanonicalTreeGeometryRoles = (
  input:
    CanonicalTreeGeometryRoleInput,
): CanonicalTreeGeometryRoleResult => {
  if (!input.projection) {
    return Object.freeze({
      roleByMemberId:
        new Map(),
      roles:
        Object.freeze([]),
      missingProjectionMemberIds:
        Object.freeze([
          ...input.treeVisibleMemberIds,
        ]),
      mutatesCanonicalGraph:
        false,
    });
  }

  const edgeById =
    new Map(
      input.edges.map(
        edge =>
          [edge.id, edge] as const,
      ),
    );

  const edgeIds =
    new Set(
      edgeById.keys(),
    );

  const roles:
    CanonicalTreeGeometryRole[] = [];

  const missingProjectionMemberIds:
    string[] = [];

  for (
    const memberId of
    input.treeVisibleMemberIds
  ) {
    const projected =
      input.projection.projections.get(
        memberId,
      );

    if (!projected) {
      missingProjectionMemberIds.push(
        memberId,
      );
      continue;
    }

    const lastStep =
      projected.connectionPath.at(-1) ??
      null;

    const anchorMemberId =
      lastStep?.fromMemberId ?? null;

    const anchorEdgeId =
      lastStep?.edgeId ?? null;

    if (
      anchorEdgeId &&
      !edgeIds.has(anchorEdgeId)
    ) {
      missingProjectionMemberIds.push(
        memberId,
      );
      continue;
    }

    const anchorEdge =
      anchorEdgeId
        ? edgeById.get(
            anchorEdgeId,
          ) ?? null
        : null;

    /*
     * CC-TREE-LIVE-003G3R
     *
     * Ordinary sibling projection does not require an extra line.
     *
     * A terminal sibling edge emitted from explicit relationship-workflow
     * authority does require lateral connector geometry because no ancestry
     * parent edge exists from which that lateral relationship can otherwise
     * be rendered.
     */
    const workflowSiblingRequiresCanonicalConnector =
      projected.connectionKind ===
        'sibling' &&
      anchorEdge?.kind ===
        'sibling' &&
      anchorEdge.source ===
        'relationship_workflow' &&
      anchorEdge.createsAncestry ===
        false &&
      anchorEdge.inheritsGeneration ===
        true &&
      anchorEdge.inheritsBranch ===
        true &&
      anchorEdge.confirmation !==
        'rejected' &&
      (
        anchorEdge.status ===
          'active' ||
        anchorEdge.status ===
          'not-applicable'
      );

    roles.push(
      Object.freeze({
        memberId,
        generation:
          projected.generation ?? 0,
        tier:
          projected.generation ?? 0,
        side:
          projected.side,
        branch:
          projected.branch,
        connectionKind:
          projected.connectionKind,
        kinshipRelationship:
          projected.kinshipRelationship,
        anchorMemberId,
        anchorEdgeId,
        sourceEdgeIds:
          Object.freeze([
            ...projected.sourceEdgeIds,
          ]),
        ancestryEdgeIds:
          Object.freeze([
            ...projected.ancestryEdgeIds,
          ]),
        affiliationEdgeIds:
          Object.freeze([
            ...projected.affiliationEdgeIds,
          ]),
        confidence:
          projected.confidence,
        requiresCanonicalConnector:
          canonicalConnectorKinds.has(
            projected.connectionKind,
          ) ||
          workflowSiblingRequiresCanonicalConnector,
        permitsNeedsConnection:
          false,
      }),
    );
  }

  const roleByMemberId =
    new Map(
      roles.map(
        role =>
          [role.memberId, role] as const,
      ),
    );

  return Object.freeze({
    roleByMemberId,
    roles:
      Object.freeze(roles),
    missingProjectionMemberIds:
      Object.freeze(
        missingProjectionMemberIds,
      ),
    mutatesCanonicalGraph:
      false,
  });
};
